/* ver 1.4 */

import * as THREE from "three";

/* ---------------------------------------------------------------------- //*
 *
 * [ << Module Function Callback Execution Order >> ]
 *
 * [<--------------- engine.awake() --------------->] <-- wait for user to call main.awake()
 *
 * - load ( loadManager )                             <-- called once, returns reference to loadManager
 * - loadProgress ( loadProgress )                    <-- called on each completed file load, returns % progress value [0-100]
 * - awake ( )                                        <-- called once
 * - earlyUpdate ( deltaTime )                        <-- called every frame after awake
 *
 * [<--------- Loading Manager Completes ---------->]
 *
 * - onLoaded ( )                                     <-- called once immediately when all assets are loaded
 *
 * [<-------------- engine.start() ---------------->] <-- wait for user to call main.start()
 *
 * - start ( )                                        <-- called once
 * - update ( deltaTime )                             <-- called every frame after start
 */

export function UnifyEngine() {
  // Settings
  let debugMode = false;
  // Vars
  let deltaTime = 0;
  let checkTimer = 1;
  let modNum = 1;
  let loadProgress = 0;
  let engineStartTime = 0;
  let errorCheck = true;
  let managerHasItemsToLoad = false;

  // Callbacks
  let modules = {};
  let asyncCallbacks = [];
  let loadProgressCallbacks = [];
  let awakeCallbacks = [];
  let startCallbacks = [];
  let earlyUpdateCallbacks = [];
  let onLoadedCallbacks = [];
  let updateCallbacks = [];
  let animatorCallbacks = [];
  let renderCallback = null;
  let startCallbackOption;
  // Logic Bools
  let loadCallbacksStarted = false;
  let allItemsDoneLoading = false;
  let awakeHasFinished = false;
  let onLoadedHasFinished = false;
  let startFinished = false;
  let startRequested = false;
  let awakeRequested = false;
  let autoStartOption = false;
  // Objects
  const clock = new THREE.Clock(false);
  const manager = new THREE.LoadingManager();
  let timeSinceAwake = 0;
  let timeSinceStart = 0;

  // ----------------------------------------------------------------------------------------------
  // Public Functions
  // ----------------------------------------------------------------------------------------------

  /**
   * Registers a module's unify callback functions
   *
   * @param {*} unifyModule
   * @param {*} moduleName
   */
  this.addModule = (unifyModule, moduleName, calledInternally) => {
    if (typeof calledInternally != "undefined") {
      if (typeof moduleName == "undefined" || typeof moduleName != "string" || moduleName == "_<unnamed>_") {
        moduleName = "_anonymous[" + modNum + "]";
        //modules[moduleName] = unifyModule; // we won't add unnamed to module references
      } else if (moduleName.includes("unify_internal")) {
        moduleName = "unify_internal";
      } else {
        modules[moduleName] = unifyModule;
      }

      // ----------------------------------------------
      if (unifyModule.hasOwnProperty("loadProgress")) {
        loadProgressCallbacks.push(unifyModule.loadProgress);
        if (debugMode && moduleName != "unify_internal") console.log("[Unify: Registered " + moduleName + ".loadProgress()");
      }
      if (unifyModule.hasOwnProperty("load")) {
        asyncCallbacks.push(unifyModule.load);
        if (debugMode && moduleName != "unify_internal") console.log("[Unify: Registered " + moduleName + ".load()");
      }
      if (unifyModule.hasOwnProperty("awake")) {
        awakeCallbacks.push(unifyModule.awake);
        if (debugMode && moduleName != "unify_internal") console.log("[Unify: Registered " + moduleName + ".awake()");
      }
      if (unifyModule.hasOwnProperty("earlyUpdate")) {
        earlyUpdateCallbacks.push(unifyModule.earlyUpdate);
        if (debugMode && moduleName != "unify_internal") console.log("[Unify: Registered " + moduleName + ".earlyUpdate()");
      }
      if (unifyModule.hasOwnProperty("onLoaded")) {
        onLoadedCallbacks.push(unifyModule.onLoaded);
        if (debugMode && moduleName != "unify_internal") console.log("[Unify: Registered " + moduleName + ".onLoaded()");
      }
      if (unifyModule.hasOwnProperty("start")) {
        startCallbacks.push(unifyModule.start);
        if (debugMode && moduleName != "unify_internal") console.log("[Unify: Registered " + moduleName + ".start()");
      }
      if (unifyModule.hasOwnProperty("update")) {
        updateCallbacks.push(unifyModule.update);
        if (debugMode && moduleName != "unify_internal") console.log("[Unify: Registered " + moduleName + ".update()");
      }
      modNum++;
    } else {
      console.error("[Unify: unify.engine.addModule is depreciated. Use unify.addModule instead.");
    }
  };
  // ------------------------------------------------

  this.getModules = () => {
    return modules;
  };

  this.addLoad = loadFunction => {
    asyncCallbacks.push(loadFunction);
  };

  this.addLoadProgress = loadProgressFunction => {
    loadProgressCallbacks.push(loadProgressFunction);
  };

  this.addAwake = awakeFunction => {
    awakeCallbacks.push(awakeFunction);
  };

  this.addOnLoaded = onLoadedFunction => {
    onLoadedCallbacks.push(onLoadedFunction);
  };

  this.addStart = startFunction => {
    startCallbacks.push(startFunction);
  };

  this.addEarlyUpdate = earlyUpdateFunction => {
    earlyUpdateCallbacks.push(earlyUpdateFunction);
  };

  this.addUpdate = updateFunction => {
    updateCallbacks.push(updateFunction);
  };

  this.setRenderCallback = func => {
    renderCallback = func;
  };

  this.removeEarlyUpdate = func => {
    for (let i = 0; i < earlyUpdateCallbacks.length; i++) {
      if (earlyUpdateCallbacks[i] === func) {
        earlyUpdateCallbacks.splice(i, 1);
        if (debugMode) console.log("[Unify: Function removed from earlyUpdate]");
      }
    }
  };

  this.removeUpdate = func => {
    for (let i = 0; i < updateCallbacks.length; i++) {
      if (updateCallbacks[i] === func) {
        updateCallbacks.splice(i, 1);
        if (debugMode) console.log("[Unify: Function removed from Update]");
      }
    }
  };

  this.addAnimator = func => {
    animatorCallbacks.push(func);
    if (debugMode) console.log("[Unify: Animator Started]");
    return animatorCallbacks.length - 1;
  };

  this.removeAnimator = func => {
    if (debugMode) console.log("[Unify: Animator ID Ended]");
    for (let i = 0; i < animatorCallbacks.length; i++) {
      if (animatorCallbacks[i] === func) {
        animatorCallbacks.splice(i, 1);
      }
    }
  };

  /**
   * Get time in seconds since last frame.
   *
   * @returns
   */
  this.getDeltaTime = () => {
    return deltaTime;
  };

  this.getTimeSinceAwake = () => {
    return timeSinceAwake;
  };

  this.getTimeSinceStart = () => {
    return timeSinceStart;
  };

  this.getTime = () => {
    return clock.getElapsedTime();
  };

  this.setDebugMode = mode => {
    debugMode = mode;
  };

  this.awake = (autoStart, startCallback) => {
    autoStartOption = autoStart;
    startCallbackOption = startCallback;
    awakeRequested = true;
  };

  this.start = startCallBack => {
    if (!startCallbackOption) startCallbackOption = startCallBack;
    if (!autoStartOption) {
      startRequested = true;
      if (debugMode && !awakeHasFinished) console.warn("[Unify: Start prevented until awake complete");
      if (debugMode && !allItemsDoneLoading) console.warn("[Unify: Start prevented until load complete");
    }
  };

  // ----------------------------------------------------------------------------------------------
  // Internal Functions
  // ----------------------------------------------------------------------------------------------
  const updateDeltaTime = timeStamp => {
    deltaTime = clock.getDelta();
    return deltaTime;
  };

  const setLoadingComplete = () => {
    if (debugMode) console.log("[Unify: [<[Load Done: @" + timeSinceAwake.toFixed(2) + "]>]");
    if (!startRequested && debugMode) {
      if (!autoStartOption && debugMode) console.log("[Unify: AutoStart is NOT enabled.");
      console.log("[Unify: Call unify.start() to continue...]");
    }
    allItemsDoneLoading = true;
  };

  // Loading manager callbacks
  manager.onProgress = (url, itemsLoaded, itemsTotal) => {
    loadProgress = (itemsLoaded / itemsTotal) * 100;
    if (debugMode) console.log(loadProgress.toFixed(0) + "% " + url);

    for (let i = 0; i < loadProgressCallbacks.length; i++) {
      loadProgressCallbacks[i](loadProgress);
    }

    if (itemsLoaded == itemsTotal) setLoadingComplete();
  };
  manager.onStart = (url, itemsLoaded, itemsTotal) => {
    setNeedToWaitForLoad();
  };

  // lets us know we need to load things, called by manager.onStart
  const setNeedToWaitForLoad = () => {
    managerHasItemsToLoad = true;
  };

  // bypass loadmanager progress check
  const nothingToLoad = () => {
    loadProgress = 100;
    for (let i = 0; i < loadProgressCallbacks.length; i++) {
      loadProgressCallbacks[i](loadProgress);
    }
    setLoadingComplete();
  };

  // -----------------
  // Frame Update Loop
  // -----------------

  const run = highResTimeStamp => {
    if (errorCheck) requestAnimationFrame(run);
    else {
      throw new Error("[Unify: HALTED - CRITICAL ERROR IN MODULE]");
    }

    if (awakeRequested) {
      errorCheck = false;
      updateDeltaTime(highResTimeStamp);
      timeSinceAwake = clock.getElapsedTime();

      // [ Load ]
      // -------------------------------------------------------
      if (!loadCallbacksStarted) {
        if (debugMode) console.log("[Unify: [<[Load]>]");

        // call all load callbacks
        clock.start();
        for (let i = 0; i < asyncCallbacks.length; i++) {
          asyncCallbacks[i](manager);
        }
        loadCallbacksStarted = true;
      }

      // [ Awake ]
      // -------------------------------------------------------

      if (!awakeHasFinished) {
        if (debugMode) console.log("[Unify: [<[Awake]>]");
        for (let i = 0; i < awakeCallbacks.length; i++) {
          awakeCallbacks[i]();
        }
        if (debugMode) console.log("[Unify: [<[EarlyUpdate]>]");
        awakeHasFinished = true;

        if (!managerHasItemsToLoad) nothingToLoad();

        if (autoStartOption) {
          startRequested = true;
        }

        if (debugMode) console.log("MAIN.MODULES contains (next line) -> ");
        if (debugMode) console.log(modules);
      }

      // [ EarlyUpdate ]
      // -------------------------------------------------------

      for (let i = 0; i < earlyUpdateCallbacks.length; i++) {
        earlyUpdateCallbacks[i](deltaTime);
      }

      // [ onLoaded ]
      // -------------------------------------------------------

      if (!onLoadedHasFinished && awakeHasFinished && allItemsDoneLoading) {
        for (let i = 0; i < onLoadedCallbacks.length; i++) {
          onLoadedCallbacks[i]();
        }
        onLoadedHasFinished = true;
      }

      // [ Animators ]
      // -------------------------------------------------------

      for (let i = 0; i < animatorCallbacks.length; i++) {
        animatorCallbacks[i](deltaTime);
      }

      // [ Load Timer ]
      // -------------------------------------------------------
      // ** ** ** ** ** ** ** ** ** ** ** ** ** ** ** ** ** **
      if (!allItemsDoneLoading) {
        if (timeSinceAwake >= checkTimer) {
          if (debugMode) console.log("[Unify: Waiting for Load..." + checkTimer.toFixed(0) + "]");
          checkTimer = timeSinceAwake + 1;
        }
      }
      // ** ** ** ** ** ** ** ** ** ** ** ** ** ** ** ** ** **
      // -------------------------------------------------------

      if (allItemsDoneLoading && awakeHasFinished && startRequested) {
        //
        // [ Start ]
        // -------------------------------------------------------

        if (!startFinished) {
          //if (debugMode) console.clear();
          engineStartTime = clock.getElapsedTime();
          if (debugMode) console.log("[Unify: [<[Start: @" + engineStartTime.toFixed(2) + "]>]");
          for (let i = 0; i < startCallbacks.length; i++) {
            startCallbacks[i]();
          }
          startFinished = true;
          if (startCallbackOption) startCallbackOption();
          if (autoStartOption && debugMode) console.log("[Unify: *AutoStart* is enabled!]");
          if (debugMode) console.log("[Unify: [<[Update]>]");
        }

        // [ Update ]
        // -------------------------------------------------------

        if (startFinished) {
          for (let i = 0; i < updateCallbacks.length; i++) {
            updateCallbacks[i](deltaTime);
          }
        }

        // Update timeSinceStart
        timeSinceStart = clock.getElapsedTime() - engineStartTime;
      }
    }
    errorCheck = true;

    if (renderCallback != null) renderCallback();
  };

  // Begin Frame Loop
  run();
}
